import Point3D, { Point3DInterface } from './Point3D';

export type Matrix = number[][];

/**
 * Provides matrix functionality to 2D arrays.
 * These methods never change array values in place.
 */
export class MatrixMethods {
  static shiftPoint(matrix: Matrix, point: Point3DInterface): Point3D {
    const { x, y, z } = point;
    const newX = matrix[0][0] * x + matrix[0][1] * y + matrix[0][2] * z + matrix[0][3];
    const newY = matrix[1][0] * x + matrix[1][1] * y + matrix[1][2] * z + matrix[1][3];
    const newZ = matrix[2][0] * x + matrix[2][1] * y + matrix[2][2] * z + matrix[2][3];
    return new Point3D(newX, newY, newZ);
  }
}

/**
 * Provides matrix functionality to 2D arrays.
 * These methods mutate array values in place.
 * These are provided for performance reasons, but use them with some caution.
 */
export class MutableMatrixMethods {
  private static preTranslateBy(matrix: Matrix, { x, y, z }: Point3DInterface) {
    matrix[0][3] += matrix[0][0] * x + matrix[0][1] * y + matrix[0][2] * z;
    matrix[1][3] += matrix[1][0] * x + matrix[1][1] * y + matrix[1][2] * z;
    matrix[2][3] += matrix[2][0] * x + matrix[2][1] * y + matrix[2][2] * z;
  }

  private static postTranslateBy(matrix: Matrix, { x, y, z }: Point3DInterface) {
    matrix[0][3] += x;
    matrix[1][3] += y;
    matrix[2][3] += z;
  }

  static conjugateByTranslation(matrix: Matrix, vector: Point3DInterface) {
    const { x, y, z } = vector;
    MutableMatrixMethods.preTranslateBy(matrix, { x: -x, y: -y, z: -z });
    MutableMatrixMethods.postTranslateBy(matrix, vector);
  }
}
